/**
 * FacilitySolve — AI Triage System v1.0
 *
 * Main page with session list and new issue flow.
 * Users report facility issues and get AI-driven diagnostic guidance
 * before escalating to a work order.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	Table,
	Tag,
	Button,
	Select,
	Input,
	Modal,
	message,
	Empty,
	Badge,
	Tooltip,
	Spin,
} from "antd";
import {
	PlusOutlined,
	BulbOutlined,
	ThunderboltOutlined,
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	ClockCircleOutlined,
	ArrowRightOutlined,
	ArrowLeftOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useFacilitySolveTriageStore } from "@/stores/facility-solve-triage-store";
import {
	getTriageSessions,
	createTriageSession,
} from "@/services/facility-solve-triage-service";
import type {
	SessionCategory,
	SessionUrgency,
	TriageSessionListItem,
} from "@/services/facility-solve-triage-service";
import "./FacilitySolve.scss";

dayjs.extend(relativeTime);

const STATUS_CONFIG: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
	ACTIVE: { color: "processing", label: "Active", icon: <ClockCircleOutlined /> },
	RESOLVED_SELF: { color: "success", label: "Self-Resolved", icon: <CheckCircleOutlined /> },
	ESCALATED: { color: "warning", label: "Escalated", icon: <ExclamationCircleOutlined /> },
	ABANDONED: { color: "default", label: "Abandoned", icon: null },
};

const URGENCY_CONFIG: Record<string, { color: string }> = {
	LOW: { color: "default" },
	MEDIUM: { color: "blue" },
	HIGH: { color: "orange" },
	CRITICAL: { color: "red" },
};

const CATEGORIES: SessionCategory[] = [
	"HVAC", "Plumbing", "Electrical", "Roofing", "Fire Protection",
	"Refrigeration", "General Maintenance", "Janitorial", "Elevator", "Other",
];

const FacilitySolveTriage = () => {
	const store = useFacilitySolveTriageStore();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const autoStarted = useRef(false);

	const [showNewModal, setShowNewModal] = useState(false);
	const [creating, setCreating] = useState(false);
	const [newForm, setNewForm] = useState({
		title: "",
		description: "",
		category: undefined as SessionCategory | undefined,
		urgency: "MEDIUM" as SessionUrgency,
	});

	useEffect(() => {
		if (autoStarted.current) return;
		const shouldAutoStart = searchParams.get("autoStart") === "1";
		const title = searchParams.get("title");
		if (!shouldAutoStart || !title) return;
		autoStarted.current = true;

		const scanId = searchParams.get("scanId");
		const locationId = searchParams.get("locationId");
		const category = searchParams.get("category") as SessionCategory | null;

		(async () => {
			setCreating(true);
			try {
				const session = await createTriageSession({
					title,
					scanId: scanId ? Number(scanId) : undefined,
					locationId: locationId ? Number(locationId) : undefined,
					category: category || undefined,
					urgency: "MEDIUM",
				});
				message.success("Triage session started");
				navigate(`/work-orders/triage/${session.id}`, { replace: true });
			} catch (err: any) {
				message.error(err.message || "Failed to start triage session");
				setCreating(false);
			}
		})();
	}, [searchParams, navigate]);

	const loadSessions = useCallback(async () => {
		store.setLoading(true);
		try {
			const sessions = await getTriageSessions(
				store.statusFilter ? { status: store.statusFilter } : undefined,
			);
			store.setSessions(sessions);
		} catch (err: any) {
			console.error("[FacilitySolve] Load error:", err);
			message.error(err?.message || "Failed to load triage sessions");
		} finally {
			store.setLoading(false);
		}
	}, [store.statusFilter]);

	useEffect(() => {
		loadSessions();
	}, [loadSessions]);

	const handleCreate = async () => {
		if (!newForm.title.trim()) {
			message.warning("Please describe the issue");
			return;
		}
		setCreating(true);
		try {
			const session = await createTriageSession({
				title: newForm.title,
				description: newForm.description || undefined,
				category: newForm.category,
				urgency: newForm.urgency,
			});
			message.success("Triage session started");
			setShowNewModal(false);
			setNewForm({ title: "", description: "", category: undefined, urgency: "MEDIUM" });
			navigate(`/work-orders/triage/${session.id}`);
		} catch (err: any) {
			message.error(err.message || "Failed to start triage");
		} finally {
			setCreating(false);
		}
	};

	const columns = [
		{
			title: "Issue",
			key: "title",
			render: (_: unknown, record: TriageSessionListItem) => (
				<div className="fst-issue-cell">
					<div className="fst-issue-title">{record.title}</div>
					{record.description && (
						<div className="fst-issue-desc">{record.description}</div>
					)}
				</div>
			),
		},
		{
			title: "Status",
			key: "status",
			width: 140,
			render: (_: unknown, record: TriageSessionListItem) => {
				const cfg = STATUS_CONFIG[record.status] ?? STATUS_CONFIG.ACTIVE;
				return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
			},
		},
		{
			title: "Urgency",
			key: "urgency",
			width: 100,
			render: (_: unknown, record: TriageSessionListItem) => {
				const cfg = URGENCY_CONFIG[record.urgency] ?? URGENCY_CONFIG.MEDIUM;
				return <Tag color={cfg.color}>{record.urgency}</Tag>;
			},
		},
		{
			title: "Category",
			key: "category",
			width: 160,
			render: (_: unknown, record: TriageSessionListItem) =>
				record.category ? <Tag>{record.category}</Tag> : <span style={{ color: "#999" }}>—</span>,
			responsive: ["md" as const],
		},
		{
			title: "Location",
			key: "location",
			width: 160,
			render: (_: unknown, record: TriageSessionListItem) =>
				record.locationName || <span style={{ color: "#999" }}>—</span>,
			responsive: ["lg" as const],
		},
		{
			title: "Created",
			key: "createdAt",
			width: 140,
			render: (_: unknown, record: TriageSessionListItem) => (
				<Tooltip title={dayjs(record.createdAt).format("MMM D, YYYY h:mm A")}>
					{dayjs(record.createdAt).fromNow()}
				</Tooltip>
			),
		},
		{
			title: "",
			key: "action",
			width: 100,
			render: (_: unknown, record: TriageSessionListItem) => (
				<Button
					type="link"
					icon={<ArrowRightOutlined />}
					onClick={() => navigate(`/work-orders/triage/${record.id}`)}
				>
					{record.status === "ACTIVE" ? "Continue" : "View"}
				</Button>
			),
		},
	];

	if (creating && searchParams.get("autoStart") === "1") {
		return (
			<div className="facility-solve-triage-page" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
				<Spin size="large" tip="Starting AI triage session..." />
			</div>
		);
	}

	return (
		<div className="facility-solve-triage-page">
			<div style={{ marginBottom: 8 }}>
				<Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate("/work-orders")}>
					Back to Work Orders
				</Button>
			</div>
			<div className="fst-header">
				<div className="fst-header-left">
					<BulbOutlined className="fst-header-icon" />
					<div>
						<h2>FacilitySolve AI Triage</h2>
						<p>AI-powered issue diagnosis and troubleshooting</p>
					</div>
				</div>
				<Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setShowNewModal(true)}>
					Report Issue
				</Button>
			</div>

			<div className="fst-toolbar">
				<Select
					placeholder="Filter by status"
					allowClear
					style={{ width: 180 }}
					value={store.statusFilter}
					onChange={(v) => store.setStatusFilter(v)}
					options={[
						{ label: "Active", value: "ACTIVE" },
						{ label: "Self-Resolved", value: "RESOLVED_SELF" },
						{ label: "Escalated", value: "ESCALATED" },
						{ label: "Abandoned", value: "ABANDONED" },
					]}
				/>
				<div className="fst-stats">
					<Badge
						count={store.sessions.filter((s) => s.status === "ACTIVE").length}
						style={{ backgroundColor: "#1890ff" }}
						showZero
					/>
					<span className="fst-stats-label">active</span>
				</div>
			</div>

			{store.sessions.length === 0 && !store.loading ? (
				<Empty
					image={<BulbOutlined style={{ fontSize: 64, color: "#d9d9d9" }} />}
					description={
						<span>
							No triage sessions yet. <a onClick={() => setShowNewModal(true)}>Report an issue</a> to get started.
						</span>
					}
					style={{ padding: "80px 0" }}
				/>
			) : (
				<Table
					dataSource={store.sessions}
					columns={columns}
					rowKey="id"
					loading={store.loading}
					size="middle"
					pagination={{ pageSize: 20, showSizeChanger: false }}
					onRow={(record) => ({
						onClick: () => navigate(`/work-orders/triage/${record.id}`),
						style: { cursor: "pointer" },
					})}
				/>
			)}

			{/* New Issue Modal */}
			<Modal
				open={showNewModal}
				onCancel={() => setShowNewModal(false)}
				title={null}
				footer={null}
				width={560}
				destroyOnClose
			>
				<div className="fst-new-modal">
					<div className="fst-new-header">
						<div className="fst-new-header-icon">
							<ThunderboltOutlined />
						</div>
						<div>
							<h3>Report a Facility Issue</h3>
							<p>Our AI will help diagnose and troubleshoot the problem</p>
						</div>
					</div>

					<div className="fst-new-body">
						<div className="fst-new-field">
							<label>What's the issue? <span className="fst-required">*</span></label>
							<Input
								value={newForm.title}
								onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
								placeholder="e.g. AC unit making loud noise on 3rd floor"
								size="large"
							/>
						</div>

						<div className="fst-new-field">
							<label>Additional Details</label>
							<Input.TextArea
								value={newForm.description}
								onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
								placeholder="Any additional context about the issue..."
								rows={3}
							/>
						</div>

						<div className="fst-new-row">
							<div className="fst-new-field">
								<label>Category</label>
								<Select
									value={newForm.category}
									onChange={(v) => setNewForm({ ...newForm, category: v })}
									placeholder="Select category"
									allowClear
									style={{ width: "100%" }}
									options={CATEGORIES.map((c) => ({ label: c, value: c }))}
								/>
							</div>
							<div className="fst-new-field">
								<label>Urgency</label>
								<Select
									value={newForm.urgency}
									onChange={(v) => setNewForm({ ...newForm, urgency: v })}
									style={{ width: "100%" }}
									options={[
										{ label: "Low", value: "LOW" },
										{ label: "Medium", value: "MEDIUM" },
										{ label: "High", value: "HIGH" },
										{ label: "Critical", value: "CRITICAL" },
									]}
								/>
							</div>
						</div>
					</div>

					<div className="fst-new-footer">
						<Button size="large" onClick={() => setShowNewModal(false)}>Cancel</Button>
						<Button type="primary" size="large" loading={creating} onClick={handleCreate} icon={<ThunderboltOutlined />}>
							Start AI Diagnosis
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export { FacilitySolveTriage };
